import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
 
const ColorLabel = ({ panColor, panNumber, caseId, onUpdate }) => {
  const [selectedColor, setSelectedColor] = useState(panColor || '#800080');
  const [currentPenNumber, setCurrentPenNumber] = useState(panNumber || '');
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
 
  const predefinedColors = [
    { name: 'Purple', value: '#800080' },
    { name: 'Magenta', value: '#FF00FF' },
    { name: 'Red', value: '#FF0000' },
    { name: 'Maroon', value: '#800000' },
    { name: 'Brown', value: '#A52A2A' },
    { name: 'Orange', value: '#FFA500' },
    { name: 'Yellow', value: '#FFFF00' },
    { name: 'Lime', value: '#00FF00' },
    { name: 'Green', value: '#008000' },
    { name: 'Olive', value: '#808000' },
    { name: 'Teal', value: '#008080' },
    { name: 'Cyan', value: '#00FFFF' },
    { name: 'Blue', value: '#0000FF' },
    { name: 'Navy', value: '#000080' },
    { name: 'Silver', value: '#C0C0C0' },
    { name: 'Gray', value: '#808080' },
    { name: 'Black', value: '#000000' }
  ];
 
  useEffect(() => {
    setSelectedColor(panColor || '#800080');
    setCurrentPenNumber(panNumber || '');
  }, [panColor, panNumber]);
 
  const handlePenNumberChange = (e) => {
    setCurrentPenNumber(e.target.value);
  };
 
  const handlePenColorChange = (newColor) => {
    setSelectedColor(newColor);
  };
 
  const openModal = () => {
    setShowModal(true);
  };
 
  const closeModal = () => {
    setShowModal(false);
    setError(null);
  };
 
  const handleSave = async () => {
    if (!caseId) {
      setError('Case ID is missing');
      return;
    }
 
    setIsLoading(true);
    setError(null);
 
    // Find the color name corresponding to the selected hex value
    const colorName = predefinedColors.find(color => color.value === selectedColor)?.name;
 
    if (!colorName) {
      setError('Color not found');
      setIsLoading(false);
      return;
    }
 
    try {
      const response = await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}/color-pan`,
        {
          color: colorName,  // Send the color name instead of hex value
          panNumber: currentPenNumber
        }
      );
 
      if (response.data && response.data.case) {
        onUpdate(response.data.case);
        closeModal();
      }
    } catch (err) {
      console.error('Error updating color/pan:', err);
      setError(err.response?.data?.message || 'Failed to update color and pan number');
    } finally {
      setIsLoading(false);
    }
  };
 
  // Find the color name from the predefined list
  const colorName = predefinedColors.find(color => color.value === selectedColor)?.name || '';
 
  return (
    <div>
      <p style={{ fontWeight: 'bold', fontSize: "19px" }}>Color:</p>
      <div style={{ display: "flex", gap: "6%" }}>
        <div style={{
          backgroundColor: selectedColor,
          width: '50px',
          height: '23px',
          border: 'none',
        }}></div>
 
        <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>{colorName}</span>
 
        <button onClick={openModal}
          style={{
            marginLeft: '10px',
            cursor: 'pointer',
            backgroundColor: '#004aad',
            color: 'white',
            padding: '5px 8px',
            borderRadius: '4px',
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderColor: "#004aad"
          }}>
          <FontAwesomeIcon icon={faPen} style={{ fontSize: '15px' }} />
        </button>
      </div>
 
      <div>
        <p style={{ fontWeight: 'bold' }}>Pen Number:</p>
        <input
          type="text"
          className='mt-2 form-control'
          value={panNumber}
          readOnly
          style={{ width: "60%",border:"none",marginTop:"0px" }}
        />
      </div>
 
      <Modal
        isOpen={showModal}
        onRequestClose={closeModal}
        contentLabel="Edit Pen Details"
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
          },
          content: {
            width: '18%',
            margin: 'auto',
            borderRadius: '4px',
            padding: '10px',
            height: '450px',
            paddingBottom: "20px"
          },
        }}
      >
        <button
          className="close-btn mb-4"
          onClick={closeModal}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: '#aaa',
            fontSize: '2em',
            float: "right"
          }}
        >
          &times;
        </button>
 
        <h5 className='mt-4'>Edit Pen Details</h5>
 
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
 
        <div style={{ marginBottom: '15px' }}>
          <label>Pen Number:</label>
          <input
            type="text"
            className='form-control'
            value={currentPenNumber}
            onChange={handlePenNumberChange}
            style={{ marginLeft: '0' }}
          />
        </div>
 
        <div>
          <label>Pen Color:</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: '10px' }}>
            {predefinedColors.map((color) => (
              <div
                key={color.name}
                style={{
                  backgroundColor: color.value,
                  width: '20px',
                  height: '20px',
                  margin: '5px',
                  cursor: 'pointer',
                  display: 'inline-block',
                  border: selectedColor === color.value ? '2px solid #004aad' : '1px solid #ddd'
                }}
                onClick={() => handlePenColorChange(color.value)}
                title={color.name}
              />
            ))}
          </div>
        </div>
 
        <button
          onClick={handleSave}
          disabled={isLoading}
          style={{
            marginTop: '15px',
            backgroundColor: '#004aad',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            cursor: 'pointer',
            borderRadius: '4px',
            width: '100%',
            opacity: isLoading ? 0.7 : 1
          }}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </button>
      </Modal>
    </div>
  );
};
 
export default ColorLabel;